import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import Loader from "../../components/ui/Loader";
import { Loader2, ArrowLeft, Users, Download, Building2, FileText, Zap, ChevronRight, Clock, RefreshCw } from "lucide-react";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-hot-toast";
import { getFullImageUrl } from "../../utils/imageHandler";

export default function AdminJobDetails() {
 const { jobId } = useParams();
 const navigate = useNavigate();
 const { user, role: myRole } = useAuth();

 const [job, setJob] = useState(null);
 const [applicants, setApplicants] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [actionLoading, setActionLoading] = useState({});
 const [canManage, setCanManage] = useState(false);

 useEffect(() => {
 fetchData();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [jobId]);

 const fetchData = async () => {
 try {
 const jobRes = await api.get(`/jobs/${jobId}`);
 const jobData = jobRes.data;
 setJob(jobData);

 const myId = user?.id;

 const ownerId = jobData?.createdById;
 const ownerRole = jobData?.createdByRole;
 const adminCanView = jobData?.adminCanView;

 const canManageAccess =
 ownerId === myId ||
 (myRole === "ADMIN" && (ownerRole === "ADMIN" || adminCanView));

 setCanManage(canManageAccess);

 if (canManageAccess) {
 const appsRes = await api.get(`/admin/jobs/${jobId}/applicants`);
 setApplicants(appsRes.data);
 } else {
 
 setApplicants([]);
 }
 } catch (err) {
 setError("Failed to load job details.");
 } finally {
 setLoading(false);
 }
 };

 const handleStatusChange = async (appId, status) => {
 if (!canManage) return;

 setActionLoading((prev) => ({ ...prev, [appId]: true }));
 try {
 await api.patch(`/admin/applications/${appId}/status`, { status });
 fetchData();
 } catch (err) {
 console.error(err);
 setError(err.response?.data?.message || "Action failed");
 } finally {
 setActionLoading((prev) => ({ ...prev, [appId]: false }));
 }
 };

 const handleRepost = async () => {
 if (!window.confirm("Are you sure you want to repost this job? It will be active for 30 days.")) return;
 
 setActionLoading((prev) => ({ ...prev, repost: true }));
 try {
 await api.post(`/admin/jobs/${jobId}/repost`);
 toast.success("Job reposted successfully! 🚀");
 fetchData();
 } catch (err) {
 console.error(err);
 toast.error("Failed to repost job");
 } finally {
 setActionLoading((prev) => ({ ...prev, repost: false }));
 }
 };

 const downloadResume = (url, filename) => {
 const link = document.createElement("a");
 link.href = url;
 link.download = filename || "resume.pdf";
 link.target = "_blank";
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 };

 if (loading) return <Loader />;

 if (error)
 return (
 <div className="flex flex-col items-center justify-center min-h-screen p-4 ">
 <p className="text-destructive">{error}</p>
 <Button onClick={() => navigate(-1)} className="mt-4">
 Go Back
 </Button>
 </div>
 );

 if (!job)
 return (
 <div className="flex flex-col items-center justify-center min-h-screen p-4 ">
 <p>Job not found</p>
 <Button onClick={() => navigate(-1)} className="mt-4">
 Go Back
 </Button>
 </div>
 );

 return (
 <div className="min-h-screen py-10 px-4">
 <div className="max-w-5xl mx-auto space-y-8">
 <Button
 variant="outline"
 onClick={() => navigate(-1)}
 className="flex items-center gap-2"
 >
 <ArrowLeft className="w-4 h-4" /> Back to Dashboard
 </Button>

 {/* Job Info */}
 <Card>
 <CardHeader className="flex flex-col sm:flex-row justify-between gap-6">
 <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
  {(job.companyLogo || job.recruiter?.logoUrl) && (
  <img 
  src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)} 
  alt="Logo" 
  className="w-20 h-20 object-contain rounded-2xl border bg-white shadow-sm shadow-indigo-100 dark:shadow-none"
  />
  )}
 <div className="space-y-1 text-center sm:text-left">
 <CardTitle className="text-3xl font-bold tracking-tight">
 {job.title}
 </CardTitle>

 <div className="flex items-center gap-2 text-muted-foreground text-sm">
 <Building2 className="w-4 h-4" />
 <span className="font-medium">
 {job.companyName || job.recruiter?.companyName}
 </span>
 <span>•</span>
 <span>{job.location}</span>
 </div>
 </div>
 </div>

 <Badge
 className={`px-4 py-1.5 text-sm font-semibold rounded-full h-fit ${
 job.status === "APPROVED"
 ? "bg-green-100 text-green-700 dark:text-green-300"
 : "bg-yellow-100 text-yellow-700 dark:text-yellow-300"
 }`}
 >
 {job.status}
 </Badge>

 {/* Repost Button */}
 {canManage && (job.status === 'EXPIRED' || job.status === 'CLOSED' || new Date(job.deadline) < new Date()) && (
 <Button 
 onClick={handleRepost} 
 disabled={actionLoading.repost}
 className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
 >
 {actionLoading.repost ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
 Repost Job
 </Button>
 )}
 </CardHeader>

 {/* Job details */}
 <CardContent className="space-y-8">
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
 <Detail label="Type" value={job.type} />
 <Detail label="Experience" value={job.experience} />
 <Detail
 label="Salary Range"
 value={`${job.salaryMin} - ${job.salaryMax} ETB`}
 />
 <Detail
 label="Posted"
 value={new Date(job.createdAt).toLocaleDateString()}
 />
 </div>

 {/* Description */}
 <div>
 <h3 className="font-semibold text-lg">
 Job Description
 </h3>
 <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
 {job.description}
 </p>
 </div>
 </CardContent>
 </Card>

 {/* Applicants Section */}
 {canManage && (
 <Card>
 <CardHeader className="border-b">
 <div className="flex items-center justify-between">
 <CardTitle className="flex items-center gap-2.5 text-xl sm:text-2xl font-bold">
 <Users className="w-6 h-6 text-primary" />
 Applicants
 <Badge variant="secondary" className="ml-2">
 {applicants.length}
 </Badge>
 </CardTitle>
 </div>
 </CardHeader>

 <CardContent className="p-4 sm:p-8 space-y-6">
 {applicants.length === 0 ? (
 <div className="text-center py-16 rounded-2xl border-2 border-dashed">
 <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
 <p className="text-lg font-medium text-muted-foreground">
 No applicants have applied yet.
 </p>
 <p className="text-sm text-muted-foreground/60 px-4">
 Wait for qualified candidates to discover this opportunity.
 </p>
 </div>
 ) : (
 <div className="grid grid-cols-1 gap-4">
 {applicants.map((app) => (
 <div
 key={app.id}
 className="group bg-card hover: border p-5 sm:p-7 rounded-2xl flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 transition-all hover:shadow-md"
 >
 {/* Applicant Info */}
 <div className="flex-1 space-y-4 w-full">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div className="space-y-1">
 <h4 className="text-xl font-bold cursor-pointer hover:text-primary flex items-center gap-2"
 onClick={() => navigate(`/admin/applications/${app.id}`)}>
 {app.fullName || app.applier?.email?.split('@')[0] || "Unknown Applicant"}
 <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
 </h4>
 <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
 <Building2 className="w-3.5 h-3.5" />
 {app.email || app.applier?.email}
 </div>
 </div>
 
 {/* Student Badges */}
 <div className="flex flex-wrap gap-2">
 {app.university && (
 <Badge variant="outline" className="bg-blue-500/10/50 border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300">
 {app.university}
 </Badge>
 )}
 {app.gpa && (
 <Badge variant="outline" className="bg-amber-500/10/50 border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300">
 GPA: {app.gpa}
 </Badge>
 )}
 {app.gender && (
 <Badge variant="outline" className="bg-purple-500/10/50 border-purple-200 text-purple-700 dark:border-purple-800 dark:text-purple-300">
 {app.gender}
 </Badge>
 )}
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 {app.coverLetter && (
 <div className="p-4 rounded-xl border">
 <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
 <FileText className="w-3 h-3 text-primary" /> Cover Letter
 </p>
 <p className="text-sm line-clamp-3 leading-relaxed">
 {app.coverLetter}
 </p>
 </div>
 )}

 {app.whyMe && (
 <div className="p-4 rounded-xl border">
 <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-2 flex items-center gap-1.5">
 <Zap className="w-3 h-3" /> Why Join?
 </p>
 <p className="text-sm line-clamp-3 leading-relaxed">
 {app.whyMe}
 </p>
 </div>
 )}
 </div>
 
 <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-bold uppercase tracking-widest">
 <Clock className="w-3 h-3" />
 Applied {new Date(app.appliedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
 </div>
 </div>

 {/* Actions */}
 <div className="flex flex-col sm:flex-row lg:flex-col items-center gap-3 w-full lg:w-48 lg:border-l lg:pl-6">
 {app.resumeUrl && (
 <Button
 variant="outline"
 size="sm"
 className="w-full"
 onClick={() => {
 const rawUrl = app.resumeUrl;
 const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, "");
 const normalizedPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
 const fullUrl = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${normalizedPath}`;
 window.open(fullUrl, '_blank');
 }}
 >
 <Download className="w-4 h-4 mr-2" /> Download Resume
 </Button>
 )}

 <div className="w-full space-y-1.5">
 <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest px-1">Application Status</p>
              <select
              value={app.status}
              onChange={(e) =>
                handleStatusChange(app.id, e.target.value)
              }
              disabled={actionLoading[app.id]}
              className={`w-full px-4 h-11 rounded-xl text-sm border font-bold transition-all cursor-pointer focus:ring-2 focus:ring-primary/50 bg-background text-foreground dark:bg-neutral-900 ${
                app.status === 'ACCEPTED'
                  ? 'bg-emerald-500/10 border-emerald-200 text-emerald-700 dark:bg-emerald-500/20 dark:border-emerald-800 dark:text-emerald-300'
                  : app.status === 'REJECTED'
                  ? 'bg-rose-500/10 border-rose-200 text-rose-700 dark:bg-rose-500/20 dark:border-rose-800 dark:text-rose-300'
                  : 'border-border dark:border-border/60 hover:border-primary/40 dark:hover:border-primary/60'
              }`}
              >
 <option value="PENDING">Pending Review</option>
 <option value="ACCEPTED">Accepted</option>
 <option value="REJECTED">Rejected</option>
 </select>
 </div>
 
 {actionLoading[app.id] && (
 <div className="flex items-center gap-2 text-xs text-primary animate-pulse font-bold">
 <Loader2 className="w-3 h-3 animate-spin" />
 Updating...
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>
 )}
 </div>
 </div>
 );
}

/* Small component cleanup */
function Detail({ label, value }) {
 return (
 <div>
 <p className="text-muted-foreground text-sm">{label}</p>
 <p className="font-semibold text-foreground">{value}</p>
 </div>
 );
}
